import React, { useEffect, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';

import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsCreateSpaceDrawerShowing } from '@confluence/create-space/entry-points/useIsCreateSpaceDrawerShowing';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { popOnboardingConfetti } from '@confluence/onboarding-helpers/entry-points/popOnboardingConfetti';

import { cards } from './assets/cards';
import {
	HTV2DownloadMobileAppCTA,
	HTV2AddProfilePictureCTA,
	HTV2ChangeSiteThemeCTA,
} from './cards/HTV2ActionCardLinkCTA';
import { HTV2ExplorePremiumCTA } from './cards/HTV2ExplorePremiumCTA';
import { HTV2CreatePageCTA, HTV2CreateWhiteboardCTA } from './cards/HTV2CreateContentCTA';
import { HTV2CreateSpaceCTA } from './cards/HTV2CreateSpaceCTA';
import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from './homeOnboardingTakeoverV2OnboardingStateKeys';
import { useIsEligibleForExplorePremiumCard } from './hooks/useIsEligibleForExplorePremiumCard';

const i18n = defineMessages({
	createPageTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.title',
		defaultMessage: 'Create a page',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createPageDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.description',
		defaultMessage:
			'Plan projects, take notes, and track tasks. Try a template or start from scratch.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createPageCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.cta',
		defaultMessage: 'Create page',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a page and land them in the editor',
	},
	createSpaceTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-space.title',
		defaultMessage: 'Set up a team space',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createSpaceDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-space.description',
		defaultMessage: 'Create a single source of truth for your team’s projects and docs.',
	},
	createWhiteboardTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.title',
		defaultMessage: 'Brainstorm on a whiteboard',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createWhiteboardDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.description',
		defaultMessage: 'Dream up new ideas and visualize your work.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createWhiteboardCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a whiteboard and land them in the whiteboard editor',
	},
	addProfilePictureTitle: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.title',
		defaultMessage: 'Add your profile photo',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	addProfilePictureDescription: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.description',
		defaultMessage: 'Welcome your teammates to Confluence with a familiar face.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	addProfilePictureCTA: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.cta',
		defaultMessage: 'Add photo',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new settings page where they can add a profile photo',
	},
	changeSiteThemeTitle: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.title',
		defaultMessage: 'Add your company’s branding',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	changeSiteThemeDescription: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.description',
		defaultMessage: 'Give Confluence a familiar look by choosing colors and adding your logo.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	changeSiteThemeCTA: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.cta',
		defaultMessage: 'Add branding',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new adminhub page where they change some site color themes and logos',
	},
	explorePremiumTitle: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.title',
		defaultMessage: 'Try Premium features',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumDescription: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.description',
		defaultMessage:
			'You’re on a {trialDaysLeft}-day free trial of Confluence Premium. See what’s included and take it out for a spin.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumCTA: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.cta',
		defaultMessage: 'See Premium features',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button open a modal that teaches them about premium features',
	},
	downloadMobileAppTitle: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.title',
		defaultMessage: 'Take Confluence on the go',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	downloadMobileAppDescription: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.description',
		defaultMessage: 'Collaborate with your team and sync work across all of your devices.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	downloadMobileAppCTA: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.cta',
		defaultMessage: 'Learn about the app',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will open a new page where they can download the mobile app',
	},
});

const ONBOARDING_ACTIONS_DATA = [
	{
		id: 'createPage',
		title: i18n.createPageTitle,
		description: i18n.createPageDescription,
		image: cards.createPage,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreatePageCTA onClick={onClick}>
				<FormattedMessage {...i18n.createPageCTA} />
			</HTV2CreatePageCTA>
		),
	},
	{
		id: 'createSpace',
		title: i18n.createSpaceTitle,
		description: i18n.createSpaceDescription,
		image: cards.createSpace,
		cta: ({ onClick }: { onClick: () => void }) => <HTV2CreateSpaceCTA onClick={onClick} />,
	},
	{
		id: 'createWhiteboard',
		title: i18n.createWhiteboardTitle,
		description: i18n.createWhiteboardDescription,
		image: cards.createWhiteboard,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreateWhiteboardCTA onClick={onClick}>
				<FormattedMessage {...i18n.createWhiteboardCTA} />
			</HTV2CreateWhiteboardCTA>
		),
	},
	{
		id: 'addProfilePicture',
		title: i18n.addProfilePictureTitle,
		description: i18n.addProfilePictureDescription,
		image: cards.addProfilePicture,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2AddProfilePictureCTA onClick={onClick}>
				<FormattedMessage {...i18n.addProfilePictureCTA} />
			</HTV2AddProfilePictureCTA>
		),
	},
	{
		id: 'changeSiteTheme',
		title: i18n.changeSiteThemeTitle,
		description: i18n.changeSiteThemeDescription,
		image: cards.changeSiteTheme,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ChangeSiteThemeCTA onClick={onClick}>
				<FormattedMessage {...i18n.changeSiteThemeCTA} />
			</HTV2ChangeSiteThemeCTA>
		),
	},
	{
		id: 'explorePremium',
		title: i18n.explorePremiumTitle,
		description: i18n.explorePremiumDescription,
		image: cards.explorePremium,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ExplorePremiumCTA isDisabled onClick={onClick}>
				<FormattedMessage {...i18n.explorePremiumCTA} />
			</HTV2ExplorePremiumCTA>
		),
	},
	{
		id: 'downloadMobileApp',
		title: i18n.downloadMobileAppTitle,
		description: i18n.downloadMobileAppDescription,
		image: cards.downloadMobileApp,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2DownloadMobileAppCTA onClick={onClick}>
				<FormattedMessage {...i18n.downloadMobileAppCTA} />
			</HTV2DownloadMobileAppCTA>
		),
	},
] as const;
export const ONBOARDING_ACTION_IDS = ONBOARDING_ACTIONS_DATA.map((action) => action.id);
export const ONBOARDING_ACTION_STATES = ['pending', 'dismissed', 'completed'] as const;

type OnboardingActionId = (typeof ONBOARDING_ACTION_IDS)[number];
type OnboardingActionState = (typeof ONBOARDING_ACTION_STATES)[number];
type OnboardingActionStates = Partial<Record<OnboardingActionId, OnboardingActionState>>;
export type OnboardingAction = {
	id: OnboardingActionId;
	image: { light: string; dark: string };
	title: MessageDescriptor;
	description: MessageDescriptor;
	cta: FC<{ onClick: () => void }>;
	state: OnboardingActionState;
};

function deserializeOnboardingActionStates(
	serializedValues: string | undefined,
): OnboardingActionStates {
	try {
		const values = JSON.parse(serializedValues || '{}');
		if (typeof values !== 'object') {
			return {};
		}

		// There's no control over the shape of the object that's deserialized,
		// so we need to filter out any keys and values that aren't valid.
		const isValidState = (value: any): value is OnboardingActionState =>
			ONBOARDING_ACTION_STATES.includes(value);

		return ONBOARDING_ACTION_IDS.reduce(
			(states, id) => (isValidState(values[id]) ? { ...states, [id]: values[id] } : states),
			{} as OnboardingActionStates,
		);
	} catch (e) {
		return {};
	}
}

function serializeOnboardingActionStates(onboardingActionStates: OnboardingActionStates) {
	return JSON.stringify(onboardingActionStates);
}

export function useOnboardingActions() {
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		...Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
		EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
	]);

	const { setOnboardingState } = useSetOnboardingState();
	const {
		isEligible: isPremiumCardEligible,
		loading: premiumCardLoading,
		formattedTrialDaysLeft,
		error: premiumCardError,
	} = useIsEligibleForExplorePremiumCard();

	const {
		onboardingActionStates: serializedOnboardingActionStates,
		hasSeenConfetti,
		hasUserPublished,
	} = deserializeState(onboardingStateData);

	const onboardingActionStates = useMemo(() => {
		return deserializeOnboardingActionStates(serializedOnboardingActionStates);
	}, [serializedOnboardingActionStates]);

	const [createSpaceDrawerOpen] = useIsCreateSpaceDrawerShowing();

	const dismiss = useMemo(
		() => (id: OnboardingActionId) => {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
				value: serializeOnboardingActionStates({ ...onboardingActionStates, [id]: 'dismissed' }),
			});
		},
		[onboardingActionStates, setOnboardingState],
	);

	const complete = useMemo(
		() => (id: OnboardingActionId) => {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
				value: serializeOnboardingActionStates({ ...onboardingActionStates, [id]: 'completed' }),
			});
		},
		[onboardingActionStates, setOnboardingState],
	);

	const errors = [premiumCardError, onboardingStateError].filter(Boolean) as ApolloError[];

	const actions: OnboardingAction[] = useMemo(
		() =>
			ONBOARDING_ACTIONS_DATA.map((action) => ({
				...action,
				state: onboardingActionStates[action.id] || 'pending',
				//  Updates the trial days left description of the explore premium card
				description:
					action.id === 'explorePremium'
						? { ...action.description, values: { trialDaysLeft: formattedTrialDaysLeft } }
						: action.description,
			})).reduce((actions: OnboardingAction[], currentAction: OnboardingAction) => {
				if (currentAction.id === 'explorePremium') {
					if (isPremiumCardEligible) {
						return [
							// Updates the CTA of the explore premium card
							...actions,
							{
								...currentAction,
								cta: ({ onClick }: { onClick: () => void }) => (
									<HTV2ExplorePremiumCTA
										onClick={onClick}
										formattedTrialDaysLeft={formattedTrialDaysLeft}
									>
										<FormattedMessage {...i18n.explorePremiumCTA} />
									</HTV2ExplorePremiumCTA>
								),
							},
						];
					} else {
						return actions; // Removes the explore premium card
					}
				}
				return [...actions, currentAction]; // Adds the rest of the cards
			}, []),
		[onboardingActionStates, formattedTrialDaysLeft, isPremiumCardEligible],
	);

	useEffect(() => {
		const hasntCreatedPage = actions.some(
			(action) => action.id === 'createPage' && action.state === 'pending',
		);

		if (hasUserPublished && hasntCreatedPage) {
			complete('createPage');
		}
	}, [hasUserPublished, complete, actions]);

	const isFinished = actions.every((action) => action.state !== 'pending');
	const hasPoppedConfetti = useRef(false);

	useEffect(() => {
		if (onboardingStateLoading || !isFinished) {
			return;
		}

		const popConfettiIfVisible = () => {
			if (
				hasSeenConfetti ||
				hasPoppedConfetti.current ||
				document.visibilityState !== 'visible' ||
				createSpaceDrawerOpen
			) {
				return;
			}

			hasPoppedConfetti.current = true;
			void popOnboardingConfetti();
			void setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasSeenConfetti,
				value: 'true',
			});
		};

		// Check to see if the user is still viewing the page. The timeout is used
		// to ensure that the confetti doesn't pop if the user is navigating away.
		const timeout = setTimeout(popConfettiIfVisible, 50);

		document.addEventListener('visibilitychange', popConfettiIfVisible);

		return () => {
			// Cleanup timeout and event listener
			clearTimeout(timeout);
			document.removeEventListener('visibilitychange', popConfettiIfVisible);
		};
	}, [
		createSpaceDrawerOpen,
		onboardingStateLoading,
		hasSeenConfetti,
		isFinished,
		setOnboardingState,
	]);

	return {
		actions,
		dismiss,
		complete,
		errors,
		loading: onboardingStateLoading || premiumCardLoading,
		isFinished,
	};
}
